import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';

import type { Membership, OrganisationLocation, OrganisationTeam } from '../domain-types';

type Assignments = Pick<Membership, 'locationIds' | 'teamIds' | 'teamLeaderTeamIds'>;
type MembershipUpdate = Partial<Pick<Membership, 'roles' | 'status'>> & Assignments;

export type MemberSecuritySummary = {
  userId: string;
  mfaEnabled: boolean | null;
  lastSeenAt?: string;
};

function activityLabel(lastSeenAt: string | undefined): string {
  if (!lastSeenAt) return 'Not recorded';
  const timestamp = Date.parse(lastSeenAt);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : 'Unavailable';
}

function selectedIds(event: ChangeEvent<HTMLSelectElement>): string[] {
  return Array.from(event.currentTarget.selectedOptions, (option) => option.value);
}

function roleLabel(role: string): string {
  return (
    {
      standard_user: 'Standard User',
      team_leader: 'Team Leader',
      manager: 'Manager',
      tenant_admin: 'Organisation Administrator',
      quality_manager: 'Manager (legacy)',
      auditor: 'Auditor (legacy)',
      viewer: 'Standard User (legacy)',
    }[role] ?? role
  );
}

function LocationTeamAssignments({
  membership,
  locations,
  teams,
  canManageAllAssignments,
  ledTeamIds,
  onUpdate,
}: {
  membership: Membership;
  locations: OrganisationLocation[];
  teams: OrganisationTeam[];
  canManageAllAssignments: boolean;
  ledTeamIds: string[];
  onUpdate: (update: MembershipUpdate) => Promise<void>;
}) {
  const visibleTeams = canManageAllAssignments
    ? teams
    : teams.filter((team) => ledTeamIds.includes(team.id));
  const canManageTeams = canManageAllAssignments || ledTeamIds.length > 0;
  if (!canManageAllAssignments && !canManageTeams) return null;

  return (
    <div className="member-assignments">
      {canManageAllAssignments ? (
        <label>
          Locations
          <select
            multiple
            value={membership.locationIds ?? []}
            onChange={(event) => void onUpdate({ locationIds: selectedIds(event) })}
          >
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {canManageTeams ? (
        <label>
          Teams
          <select
            multiple
            value={membership.teamIds ?? []}
            onChange={(event) => {
              const next = selectedIds(event);
              const preserved = (membership.teamIds ?? []).filter((id) => !ledTeamIds.includes(id));
              void onUpdate({ teamIds: canManageAllAssignments ? next : [...preserved, ...next] });
            }}
          >
            {visibleTeams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {canManageAllAssignments && membership.roles.includes('team_leader') ? (
        <label>
          Teams led
          <select
            multiple
            value={membership.teamLeaderTeamIds ?? []}
            onChange={(event) => void onUpdate({ teamLeaderTeamIds: selectedIds(event) })}
          >
            {(membership.teamIds ?? []).map((teamId) => {
              const team = teams.find((candidate) => candidate.id === teamId);
              return team ? (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ) : null;
            })}
          </select>
        </label>
      ) : null}
    </div>
  );
}

export function TenantMembersPanel({
  visible,
  canAdminister,
  canManageAllAssignments,
  ledTeamIds,
  members,
  securitySummaries,
  locations,
  teams,
  inviteEmail,
  inviteRole,
  onInviteEmailChange,
  onInviteRoleChange,
  onInvite,
  onUpdateMembership,
  onResetMfa,
}: {
  visible: boolean;
  canAdminister: boolean;
  canManageAllAssignments: boolean;
  ledTeamIds: string[];
  members: Membership[];
  securitySummaries?: MemberSecuritySummary[];
  locations: OrganisationLocation[];
  teams: OrganisationTeam[];
  inviteEmail: string;
  inviteRole: string;
  onInviteEmailChange: (value: string) => void;
  onInviteRoleChange: (value: string) => void;
  onInvite: (event: FormEvent<HTMLFormElement>, assignments: Assignments) => Promise<void>;
  onUpdateMembership: (membership: Membership, update: MembershipUpdate) => Promise<void>;
  onResetMfa?: (membership: Membership) => Promise<void>;
}) {
  const [inviteLocationIds, setInviteLocationIds] = useState<string[]>([]);
  const [inviteTeamIds, setInviteTeamIds] = useState<string[]>([]);
  if (!visible) return null;

  return (
    <section className="members-panel" aria-labelledby="members-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Personnel</p>
          <h2 id="members-title">Organisation personnel</h2>
        </div>
        <span>{members.length} people</span>
      </div>
      {canAdminister ? (
        <form
          className="personnel-invite-form"
          onSubmit={(event) =>
            void onInvite(event, {
              locationIds: inviteLocationIds,
              teamIds: inviteTeamIds,
              ...(inviteRole === 'team_leader' ? { teamLeaderTeamIds: inviteTeamIds } : {}),
            })
          }
        >
          <input
            aria-label="Personnel email"
            required
            type="email"
            placeholder="person@example.com"
            value={inviteEmail}
            onChange={(event) => onInviteEmailChange(event.target.value)}
          />
          <select
            aria-label="Personnel role"
            value={inviteRole}
            onChange={(event) => onInviteRoleChange(event.target.value)}
          >
            <option value="standard_user">Standard User</option>
            <option value="team_leader">Team Leader</option>
            <option value="manager">Manager</option>
            <option value="tenant_admin">Organisation Administrator</option>
          </select>
          <label>
            Locations
            <select
              multiple
              value={inviteLocationIds}
              onChange={(event) => setInviteLocationIds(selectedIds(event))}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Teams
            <select
              multiple
              value={inviteTeamIds}
              onChange={(event) => setInviteTeamIds(selectedIds(event))}
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>
          <button type="submit">Invite person</button>
        </form>
      ) : null}
      <div className="member-list">
        {members.map((membership) => {
          const security = securitySummaries?.find((item) => item.userId === membership.userId);
          return (
            <article key={membership.userId}>
              <div>
                <strong>{membership.email}</strong>
                <small>{membership.roles.map(roleLabel).join(', ')}</small>
                <small>
                  Organisation access: {membership.status}
                  {membership.createdAt
                    ? ` · Joined ${new Date(membership.createdAt).toLocaleDateString()}`
                    : ''}
                </small>
                {securitySummaries ? (
                  <div
                    aria-label="Account security summary"
                    className="member-security-summary"
                    role="group"
                  >
                    <small>
                      Authenticator MFA:{' '}
                      {security?.mfaEnabled === true
                        ? 'Enabled'
                        : security?.mfaEnabled === false
                          ? 'Not enabled'
                          : 'Unavailable'}
                    </small>
                    <small>Last app activity: {activityLabel(security?.lastSeenAt)}</small>
                  </div>
                ) : null}
                <small>
                  Locations:{' '}
                  {(membership.locationIds ?? [])
                    .map((id) => locations.find((location) => location.id === id)?.name)
                    .filter(Boolean)
                    .join(', ') || 'None'}
                </small>
                <small>
                  Teams:{' '}
                  {(membership.teamIds ?? [])
                    .map((id) => teams.find((team) => team.id === id)?.name)
                    .filter(Boolean)
                    .join(', ') || 'None'}
                </small>
                <LocationTeamAssignments
                  membership={membership}
                  locations={locations}
                  teams={teams}
                  canManageAllAssignments={canManageAllAssignments}
                  ledTeamIds={ledTeamIds}
                  onUpdate={(update) => onUpdateMembership(membership, update)}
                />
              </div>
              {canAdminister ? (
                <div className="member-admin-actions">
                  <label>
                    Role
                    <select
                      value={membership.roles[0] ?? 'standard_user'}
                      onChange={(event) =>
                        void onUpdateMembership(membership, {
                          roles: [event.target.value],
                          ...(event.target.value !== 'team_leader'
                            ? { teamLeaderTeamIds: [] }
                            : {}),
                        })
                      }
                    >
                      <option value="standard_user">Standard User</option>
                      <option value="team_leader">Team Leader</option>
                      <option value="manager">Manager</option>
                      <option value="tenant_admin">Organisation Administrator</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      void onUpdateMembership(membership, {
                        status: membership.status === 'disabled' ? 'active' : 'disabled',
                      })
                    }
                  >
                    {membership.status === 'disabled' ? 'Reactivate' : 'Disable'}
                  </button>
                  {onResetMfa && membership.status !== 'disabled' ? (
                    <button type="button" onClick={() => void onResetMfa(membership)}>
                      Reset MFA
                    </button>
                  ) : null}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function OrganisationStructurePanel({
  visible,
  locations,
  teams,
  busy,
  onSave,
}: {
  visible: boolean;
  locations: OrganisationLocation[];
  teams: OrganisationTeam[];
  busy: boolean;
  onSave: (structure: {
    locations: OrganisationLocation[];
    teams: OrganisationTeam[];
  }) => Promise<void>;
}) {
  const [draftLocations, setDraftLocations] = useState(locations);
  const [draftTeams, setDraftTeams] = useState(teams);
  const [locationName, setLocationName] = useState('');
  const [addressLines, setAddressLines] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('Australia');
  const [teamName, setTeamName] = useState('');
  const [teamLocationIds, setTeamLocationIds] = useState<string[]>([]);

  useEffect(() => setDraftLocations(locations), [locations]);
  useEffect(() => setDraftTeams(teams), [teams]);

  if (!visible) return null;

  const addLocation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const lines = addressLines
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (
      !locationName.trim() ||
      lines.length === 0 ||
      !suburb.trim() ||
      !state.trim() ||
      !postcode.trim()
    )
      return;
    setDraftLocations((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        name: locationName.trim(),
        addressLines: lines,
        suburb: suburb.trim(),
        state: state.trim(),
        postcode: postcode.trim(),
        country: country.trim() || 'Australia',
      },
    ]);
    setLocationName('');
    setAddressLines('');
    setSuburb('');
    setState('');
    setPostcode('');
  };

  const addTeam = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!teamName.trim()) return;
    setDraftTeams((current) => [
      ...current,
      { id: crypto.randomUUID(), name: teamName.trim(), locationIds: teamLocationIds },
    ]);
    setTeamName('');
    setTeamLocationIds([]);
  };

  return (
    <section
      className="organisation-structure-panel"
      aria-labelledby="organisation-structure-title"
    >
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Organisation structure</p>
          <h2 id="organisation-structure-title">Locations and teams</h2>
        </div>
      </div>
      <p>
        Locations and teams are private to this organisation. Personnel may belong to more than one
        of each.
      </p>
      <form className="location-form" onSubmit={addLocation}>
        <input
          aria-label="Location name"
          required
          placeholder="Location name"
          value={locationName}
          onChange={(event) => setLocationName(event.target.value)}
        />
        <textarea
          aria-label="Physical address"
          required
          placeholder="Physical address (one line per address line)"
          value={addressLines}
          onChange={(event) => setAddressLines(event.target.value)}
        />
        <input
          aria-label="Suburb"
          required
          placeholder="Suburb"
          value={suburb}
          onChange={(event) => setSuburb(event.target.value)}
        />
        <input
          aria-label="State or territory"
          required
          placeholder="State / territory"
          value={state}
          onChange={(event) => setState(event.target.value)}
        />
        <input
          aria-label="Postcode"
          required
          placeholder="Postcode"
          value={postcode}
          onChange={(event) => setPostcode(event.target.value)}
        />
        <input
          aria-label="Country"
          required
          placeholder="Country"
          value={country}
          onChange={(event) => setCountry(event.target.value)}
        />
        <button type="submit">Add location</button>
      </form>
      <div className="structure-list">
        {draftLocations.map((location) => (
          <article key={location.id}>
            <div>
              <strong>{location.name}</strong>
              <small>
                {[
                  ...location.addressLines,
                  location.suburb,
                  location.state,
                  location.postcode,
                  location.country,
                ].join(', ')}
              </small>
            </div>
            <button
              type="button"
              onClick={() =>
                setDraftLocations((items) => items.filter((item) => item.id !== location.id))
              }
            >
              Remove
            </button>
          </article>
        ))}
      </div>
      <form className="team-form" onSubmit={addTeam}>
        <input
          aria-label="Custom team name"
          required
          placeholder="Custom team name"
          value={teamName}
          onChange={(event) => setTeamName(event.target.value)}
        />
        <label>
          Associated locations
          <select
            multiple
            value={teamLocationIds}
            onChange={(event) => setTeamLocationIds(selectedIds(event))}
          >
            {draftLocations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
        <button type="submit">Add team</button>
      </form>
      <div className="structure-list">
        {draftTeams.map((team) => (
          <article key={team.id}>
            <div>
              <strong>{team.name}</strong>
              <small>
                {team.locationIds
                  .map((id) => draftLocations.find((location) => location.id === id)?.name)
                  .filter(Boolean)
                  .join(', ') || 'No location restriction'}
              </small>
            </div>
            <button
              type="button"
              onClick={() => setDraftTeams((items) => items.filter((item) => item.id !== team.id))}
            >
              Remove
            </button>
          </article>
        ))}
      </div>
      <button
        disabled={busy}
        type="button"
        onClick={() => void onSave({ locations: draftLocations, teams: draftTeams })}
      >
        {busy ? 'Saving…' : 'Save organisation structure'}
      </button>
    </section>
  );
}
